<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <form @submit="createIndex" class="flex flex-col h-full">
      <div v-bar class="flex-1 min-h-0 overflow-y-auto">
        <div class="min-h-full">
          <h3 class="mt-8 font-semibold px-8">Create New Knowledge Base</h3>

        <div class="mt-3">
          <with-error-message :show-error="isIndexNameError" class="border-solid border-b border-0 border-gray-border px-8 pb-5">
            <base-label id="indexName" label="Knowledge Base Name" :label-classes="'font-medium'"/>
            <base-input id="indexName"
                        name="indexName"
                        placeholder="e.g. Sales campaign"
                        variant="editor"
                        type="string"
                        class="w-full border-gray-border mt-1 h-10"
                        :class="{'border-focus-error': isIndexNameError}"
                        v-model.trim="indexName"
            />
          </with-error-message>

          <div class="mt-3">
            <div class="px-8">
              <with-error-message :show-error="isIndexSourceNameError">
                <base-label id="indexSourceName" label="Source Name" :label-classes="'font-medium'"/>
                <base-input id="indexSourceName"
                            name="indexSourceName"
                            placeholder="e.g. Sales campaign feed"
                            variant="editor"
                            type="string"
                            class="w-full border-gray-border py-px mt-1 h-10"
                            :class="{'border-focus-error': isIndexSourceNameError}"
                            v-model.trim="indexSourceName"
                />
              </with-error-message>

              <div class="mt-5">
                <base-label label="Data source"
                            :label-classes="'font-medium'"
                            id="data-source"
                />
                <BaseToggleGroup class="mt-1" :tabs="formsOptions" v-model="sourceType" />
                <settings-indexes-forms class="mt-4"
                                        :fileSettings.sync="fileSettings"
                                        :active-form.sync="sourceType"
                                        :xmlFeedSettings.sync="xmlFeedSettings"
                                        :webPageSettings.sync="webPageSettings"
                />
              </div>
            </div>

            <settings-index-scheduler :schedule-params="scheduleParams"
                                      :is-create="true"
                                      @update-schedule-params="updateScheduleParams"
                                      class="mt-6 pt-4 px-8 pb-8 border-solid border-t border-0 border-gray-border"
            />
          </div>
        </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-6 px-8 flex-shrink-0">
        <busy-button id="createButton"
                     :variant="createButtonVariant"
                     class="col-span-1"
                     :is-loading="isLoading"
                     :is-disabled="isLoading"
                     @click="createIndex"
        >
          <span class="w-full">{{ createButtonText }}</span>
        </busy-button>
      </div>
    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import SettingsIndexesXmlFeed from '@/components/settings/indexes/SettingsIndexesXmlFeed.vue'
import SettingsIndexScheduler from '@/components/settings/indexes/SettingsIndexScheduler.vue'
import indexesService from '@/services/amio/indexes.service'
import errorUtils from '@/utils/error.utils'
import toaster from '@/utils/toaster'
import previewModule from '@/store/modules/preview.module'
import SettingsIndexesForms from '@/components/settings/indexes/SettingsIndexesForms.vue'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import fileService from '@/services/amio/file.service'
import head from 'ramda/src/head'
import { SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { CREATE_BUTTON_LABELS } from '@/utils/text.constants'

const FORMS = Object.freeze([
  { key: KNOWLEDGE_BASES.XML_FEED, label: 'XML Feed' },
  { key: KNOWLEDGE_BASES.FILE, label: 'File' },
  { key: KNOWLEDGE_BASES.WEB_PAGE, label: 'Web Page' }
])

export default {
  name: 'SettingsIndexCreate',
  components: {
    SettingsIndexesForms,
    BusyButton,
    SettingsIndexesXmlFeed,
    SettingsIndexScheduler,
    WithErrorMessage
  },
  data() {
    return {
      indexName: '',
      indexSourceName: '',
      webPageSettings: {
        url: '',
        prompt: null,
        format: 'txt',
        headerKey: null,
        headerValue: null
      },
      xmlFeedSettings: {
        url: '',
        item: '',
        itemName: '',
        itemId: '',
        vectorizationSkipTags: []
      },
      fileSettings: {
        file: null,
        url: ''
      },
      scheduleParams: null,
      isLoading: false,
      sourceType: KNOWLEDGE_BASES.XML_FEED,
      createButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      createButtonText: CREATE_BUTTON_LABELS.DEFAULT
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    isIndexNameError() {
      return this.errorDescription?.includes('Knowledge Base Name')
    },

    isIndexSourceNameError() {
      return this.errorDescription?.includes('Source Name')
    },

    formsOptions() {
      return FORMS
    }
  },
  methods: {
    updateScheduleParams(scheduleParams) {
      this.scheduleParams = scheduleParams
    },
    createIndex(e) {
      e.preventDefault()
      if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
        this.createIndexWithXMLSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.FILE) {
        this.createIndexWithFileSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
        this.createIndexWithWebPageSource()
      }
    },
    async createIndexWithFileSource() {
      this.isLoading = true
      try {
        const source = { type: KNOWLEDGE_BASES.FILE, name: this.indexSourceName }
        if (this.fileSettings.file) {
          const response = await fileService.create(this.fileSettings.file)
          source.format = response.format
          source.url = response.url
        } else {
          source.format = this.fileSettings.url.split('.').pop()
          source.url = this.fileSettings.url
        }
        const newIndex = await indexesService.create(this.indexName, [source], this.scheduleParams)
        toaster.info('Creating knowledge base... it may take a few minutes.')
        this.error = null
        this.$emit('process-new-index', newIndex)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },
    async createIndexWithWebPageSource() {
      this.isLoading = true
      try {
        const source = {
          type: 'web_scraping',
          format: 'html',
          name: this.indexSourceName,
          url: this.webPageSettings.url,
          config: {
            prompt: this.webPageSettings.prompt,
            format: this.webPageSettings.format
          }
        }
        if (this.webPageSettings.headerKey !== null || this.webPageSettings.headerValue !== null) {
          source.config.header = {
            key: this.webPageSettings.headerKey,
            value: this.webPageSettings.headerValue
          }
        }
        const newIndex = await indexesService.create(this.indexName, [source], this.scheduleParams)
        toaster.info('Creating knowledge base... it may take a few minutes.')
        this.error = null
        this.$emit('process-new-index', newIndex)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },
    async createIndexWithXMLSource() {
      this.isLoading = true
      try {
        const { url, item, itemName, itemId } = this.xmlFeedSettings
        const source = {
          type: 'url',
          format: 'xml',
          name: this.indexSourceName,
          config: {
            tag_mapping: {
              item: item,
              item_name: itemName,
              item_id: itemId
            }
          },
          url
        }
        const newIndex = await indexesService.create(this.indexName, [source], this.scheduleParams)
        toaster.info('Creating knowledge base... it may take a few minutes.')
        this.error = null
        this.$emit('process-new-index', newIndex)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.createButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      this.createButtonText = CREATE_BUTTON_LABELS[buttonState]
    },

    handleError(err) {
      if (err.message === 'Payload Too Large') {
        this.error = { description: 'File exceeds 1 MB limit.' }
        return
      }

      const errorMessage = head(err.response.data.details.errors)?.message || err.message
      if (errorUtils.isIndexCreateUpdateError(errorMessage)) {
        this.error = { description: errorMessage }
        return
      }

      this.error = errorMessage
      toaster.error(errorMessage)
    },

    mounted() {
      this.error = null
    }

  }
}
</script>
